import React, { useState, useCallback, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import RotateRightIcon from "@material-ui/icons/RotateRight";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import clsx from "clsx";

const useStyles = makeStyles(() => ({
  // ── Thumbnail inside the message bubble ──────────────────────────────────
  messageMedia: {
    objectFit: "cover",
    width: "100%",
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    cursor: "zoom-in",
    display: "block",
  },

  messageMediaSticker: {
    width: 200,
    height: 200,
    objectFit: "contain",
  },

  messageMediaDeleted: {
    filter: "grayscale(1)",
    opacity: 0.4,
    cursor: "default",
  },

  // ── Full-screen overlay ───────────────────────────────────────────────────
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    backdropFilter: "blur(4px)",
    zIndex: 1400,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "zoom-out",
  },

  // ── Top toolbar: filename + open-in-new (left), close (right) ────────────
  topBar: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    // viewport-fit=cover puts top:0 UNDER the iOS notch/status bar, occluding the
    // filename + close (×). Extend the bar past the inset so its content clears
    // the notch. env()=0 on non-notched/desktop, so this is inert there.
    height: "calc(52px + env(safe-area-inset-top, 0px))",
    paddingTop: "env(safe-area-inset-top, 0px)",
    paddingLeft: 14,
    paddingRight: 14,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    zIndex: 1402,
    background: "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 100%)",
  },

  topBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },

  topBarRight: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },

  fileName: {
    color: "rgba(255,255,255,0.85)",
    fontSize: 13,
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "50vw",
    userSelect: "none",
  },

  // ── Bottom toolbar: rotate + download ────────────────────────────────────
  bottomBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    // Clear the Android gesture bar / iOS home indicator so rotate+download
    // aren't half-hidden under it. env()=0 where there's no inset.
    height: "calc(60px + env(safe-area-inset-bottom, 0px))",
    paddingBottom: "env(safe-area-inset-bottom, 0px)",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    zIndex: 1402,
    background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)",
  },

  // ── Toolbar buttons (shared style) ───────────────────────────────────────
  toolbarBtn: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "none",
    backgroundColor: "rgba(255,255,255,0.15)",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.18s ease",
    textDecoration: "none",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.32)",
    },
    "&:active": {
      backgroundColor: "rgba(255,255,255,0.48)",
    },
  },

  toolbarBtnLabel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    cursor: "pointer",
  },

  btnLabel: {
    fontSize: 10,
    color: "rgba(255,255,255,0.7)",
    userSelect: "none",
    letterSpacing: 0.3,
  },

  // ── Full-size image ───────────────────────────────────────────────────────
  fullImage: {
    maxWidth: "90vw",
    maxHeight: "80vh",
    objectFit: "contain",
    borderRadius: 4,
    boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
    cursor: "default",
    userSelect: "none",
    transition: "transform 0.25s ease",
  },
}));

/**
 * ModalImageCors — image lightbox with rotate and cross-origin download.
 *
 * Features:
 *  - Dark overlay with blur backdrop (rgba(0,0,0,0.85) + backdropFilter:blur(4px))
 *  - Top bar: filename + open-in-new  |  close (×)
 *  - Image centred with CSS transform rotation (state-driven)
 *  - Bottom bar: rotate-left | rotate-right | download
 *  - Download: fetch → Blob → createObjectURL → <a>.click()
 *    This bypasses browser cross-origin restrictions because the fetch
 *    travels through the browser normally and the resulting blob is local.
 *    Falls back to window.open if the fetch fails.
 *  - ESC closes | click outside image closes
 */
const ModalImageCors = ({ imageUrl, isDeleted, data, onImgLoad }) => {
  const classes = useStyles();
  const [open, setOpen]       = useState(false);
  const [rotation, setRotation] = useState(0);
  const [downloading, setDownloading] = useState(false);

  const isSticker = data?.message && "stickerMessage" in data.message;

  const handleOpen = useCallback(() => {
    if (!isDeleted && imageUrl) {
      setRotation(0);
      setOpen(true);
    }
  }, [isDeleted, imageUrl]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setRotation(0);
  }, []);

  // Keyboard: Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  // Derive a clean filename from the URL path
  const fileName = imageUrl
    ? (imageUrl.split("/").pop()?.split("?")[0] || "image")
    : "image";

  // Rotate left / right (90° steps)
  const rotateLeft  = useCallback((e) => { e.stopPropagation(); setRotation((r) => r - 90); }, []);
  const rotateRight = useCallback((e) => { e.stopPropagation(); setRotation((r) => r + 90); }, []);

  // Blob download — bypasses cross-origin restrictions
  const handleDownload = useCallback(async (e) => {
    e.stopPropagation();
    if (!imageUrl || downloading) return;
    setDownloading(true);
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab (user can right-click save)
      window.open(imageUrl, "_blank");
    } finally {
      setDownloading(false);
    }
  }, [imageUrl, fileName, downloading]);

  return (
    <>
      {/* ── Thumbnail ──────────────────────────────────────────────────── */}
      <img
        src={imageUrl}
        loading="lazy"
        decoding="async"
        alt=""
        className={clsx(classes.messageMedia, {
          [classes.messageMediaSticker]: isSticker,
          [classes.messageMediaDeleted]: isDeleted,
        })}
        onClick={handleOpen}
        onLoad={onImgLoad}
      />

      {/* ── Lightbox ───────────────────────────────────────────────────── */}
      {open && (
        <div className={classes.overlay} onClick={handleClose}>

          {/* Top bar — stop propagation so clicks don't close */}
          <div className={classes.topBar} onClick={(e) => e.stopPropagation()}>
            <div className={classes.topBarLeft}>
              <span className={classes.fileName} title={fileName}>
                {fileName}
              </span>
              <a
                href={imageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.toolbarBtn}
                title="Open in new tab"
              >
                <OpenInNewIcon style={{ fontSize: 16 }} />
              </a>
            </div>
            <div className={classes.topBarRight}>
              <button
                type="button"
                className={classes.toolbarBtn}
                onClick={handleClose}
                title="Close (Esc)"
              >
                <CloseIcon style={{ fontSize: 18 }} />
              </button>
            </div>
          </div>

          {/* Full-size image — stop click so overlay doesn't fire */}
          <img
            src={imageUrl}
            loading="lazy"
            decoding="async"
            alt=""
            className={classes.fullImage}
            style={{ transform: `rotate(${rotation}deg)` }}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Bottom bar — rotate + download */}
          <div className={classes.bottomBar} onClick={(e) => e.stopPropagation()}>

            <div className={classes.toolbarBtnLabel}>
              <button
                type="button"
                className={classes.toolbarBtn}
                onClick={rotateLeft}
                title="Rotate left"
              >
                <RotateLeftIcon style={{ fontSize: 20 }} />
              </button>
              <span className={classes.btnLabel}>↺</span>
            </div>

            <div className={classes.toolbarBtnLabel}>
              <button
                type="button"
                className={classes.toolbarBtn}
                onClick={rotateRight}
                title="Rotate right"
              >
                <RotateRightIcon style={{ fontSize: 20 }} />
              </button>
              <span className={classes.btnLabel}>↻</span>
            </div>

            <div className={classes.toolbarBtnLabel}>
              <button
                type="button"
                className={classes.toolbarBtn}
                onClick={handleDownload}
                disabled={downloading}
                title="Download"
                style={{ opacity: downloading ? 0.6 : 1 }}
              >
                <GetAppIcon style={{ fontSize: 20 }} />
              </button>
              <span className={classes.btnLabel}>⬇</span>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default ModalImageCors;
